import React from 'react';

/**
 * KPI Row — top 4 metric cards with % change, trend indicator, and inline insight.
 *
 * Field mapping from /dashboard/overview API:
 *   total_revenue_month   — period revenue
 *   gross_profit_period   — period gross profit (revenue - COGS)
 *   revenue_change_pct    — % change vs previous period
 *   profit_change_pct     — % change vs previous period
 *   total_sales_today     — today's order count
 *   total_revenue_today   — today's revenue
 *   pending_orders        — pending supplier orders
 */

const TrendIcon = ({ positive }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: positive ? 'none' : 'rotate(180deg)' }}>
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const KPICard = ({ label, value, insight, change, loading, prefix = '', icon, tooltip }) => {
  const isPositive = change >= 0;
  const hasChange = change != null && !isNaN(change);

  if (loading) {
    return (
      <div className="kpi-card kpi-card--loading">
        <div className="skeleton skeleton--label" />
        <div className="skeleton skeleton--value" />
        <div className="skeleton skeleton--sub" />
      </div>
    );
  }

  return (
    <div className="kpi-card">
      <div className="kpi-card__header">
        <span className="kpi-card__label">
          {label}
          {tooltip && (
            <span className="kpi-card__tooltip" title={tooltip}>ⓘ</span>
          )}
        </span>
        {icon && <span className="kpi-card__icon">{icon}</span>}
      </div>
      <div className="kpi-card__value">
        {prefix}{value ?? '—'}
      </div>
      <div className="kpi-card__footer">
        {hasChange && (
          <span className={`kpi-trend ${isPositive ? 'kpi-trend--up' : 'kpi-trend--down'}`}>
            <TrendIcon positive={isPositive} />
            {Math.abs(change).toFixed(1)}%
          </span>
        )}
      </div>
      {insight && (
        <p className="kpi-card__insight">{insight}</p>
      )}
    </div>
  );
};

/** Derive a short, plain-language insight from the metric values. */
function deriveInsight(type, data, prefix, days) {
  if (type === 'revenue') {
    const pct = data?.revenue_change_pct;
    if (pct == null) return `No comparison data yet`;
    if (pct < -10) return `Down ${Math.abs(pct).toFixed(1)}% — consider running a promotion`;
    if (pct < -5)  return `Eased ${Math.abs(pct).toFixed(1)}% vs last period`;
    if (pct > 10)  return `Up ${pct.toFixed(1)}% — strong growth this period`;
    return `Stable vs previous ${days}-day period`;
  }
  if (type === 'profit') {
    const pct = data?.profit_change_pct;
    if (pct == null) return `Revenue minus cost of goods sold`;
    if (pct < -10) return `Margin dropped ${Math.abs(pct).toFixed(1)}% — review product costs`;
    if (pct > 10)  return `Margin up ${pct.toFixed(1)}% — great efficiency`;
    return `Stable margin vs last period`;
  }
  if (type === 'sales_today') {
    const count = data?.total_sales_today ?? 0;
    const rev = data?.total_revenue_today;
    if (count === 0) return `No sales recorded yet today`;
    const revStr = rev != null ? ` · ${prefix}${Number(rev).toLocaleString(undefined, { maximumFractionDigits: 0 })} revenue` : '';
    return `${count} order${count !== 1 ? 's' : ''}${revStr} today`;
  }
  if (type === 'pending_orders') {
    const n = data?.pending_orders ?? 0;
    if (n === 0) return `All orders up to date`;
    return `${n} order${n !== 1 ? 's' : ''} need${n === 1 ? 's' : ''} your attention`;
  }
  return null;
}

const DashboardKPIRow = ({ data, loading, currency = '', days = 30 }) => {
  const fmt = (v) => v != null
    ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })
    : null;

  const pfx = currency ? `${currency} ` : '';
  const periodLabel = `vs previous ${days}-day period`;

  const kpis = [
    {
      type: 'revenue',
      label: `Revenue (${days}d)`,
      value: fmt(data?.total_revenue_month),
      prefix: pfx,
      change: data?.revenue_change_pct,
      subLabel: periodLabel,
      icon: '💰',
      insight: deriveInsight('revenue', data, pfx, days),
    },
    {
      type: 'profit',
      label: 'Gross Profit',
      value: fmt(data?.gross_profit_period),
      prefix: pfx,
      change: data?.profit_change_pct,
      subLabel: periodLabel,
      icon: '📈',
      tooltip: 'Revenue minus the cost of goods sold (COGS)',
      insight: deriveInsight('profit', data, pfx, days),
    },
    {
      type: 'sales_today',
      label: 'Sales Today',
      value: fmt(data?.total_sales_today),
      change: null,
      icon: '🛒',
      insight: deriveInsight('sales_today', data, pfx, days),
    },
    {
      type: 'pending_orders',
      label: 'Pending Orders',
      value: fmt(data?.pending_orders),
      change: null,
      icon: '📦',
      insight: deriveInsight('pending_orders', data, pfx, days),
    },
  ];

  return (
    <div className="dashboard-kpi-row">
      {kpis.map((kpi) => (
        <KPICard key={kpi.label} {...kpi} loading={loading} />
      ))}
    </div>
  );
};

export default DashboardKPIRow;
